import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import ChariotConnect from 'react-chariot-connect'

import InvalidForm from 'src/components/InvalidForm'
import csrfHeaders from 'src/utils/csrfHeaders'

// Single submit path: every donation eventually flows through
// PaymentProcessors::DafDonationsController#create with an
// `order_id + Chariot onSuccess payload`. The controller hydrates
// the order, calls ChariotApi inline (Chariot is sync — no webhook),
// and queues the confirmation mailer. :direct orgs finish at :processed;
// special_daf orgs rest at :pledged for staff conversion — nothing here
// branches on status. In twoStep mode Launcher's Continue runs saveDraft
// up front and threads orderToken in. In single-step mode onSuccess
// JIT-runs saveDraft itself via the `record` prop before the same POST.
const Daf = ({
  partnerItem, i18n, initUrl, submitUrl, setDonation,
  orderToken, record, paymentMethod,
}) => {
  const [ connectId, setConnectId ] = useState(null)
  const [ apiKey, setApiKey ] = useState(null)
  const [ loading, setLoading ] = useState(true)
  const [ errors, setErrors ] = useState()

  useEffect(() => {
    if (partnerItem.beneficiary.id == '_multiple') return

    const ein = partnerItem.beneficiary.ein.replace(/-/g, '')
    setLoading(true)

    fetch(initUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ein })
    })
    .then(response => response.json().then(data => {
      if (response.ok) {
        setConnectId(data.connect_id)
        setApiKey(data.api_key)
      } else {
        setErrors({ base: [ 'There was a network error. Please try again.' ] })
      }
    })
    .catch(error => {
      console.error('Fetch error:', error)
      setErrors({ base: [ 'Something went wrong. Please reload the page and try again.' ] })
    }))
    .finally(() => {
      setLoading(false)
    })
  }, [ partnerItem.beneficiary.id ])

  const onSuccess = async chariotResponse => {
    setLoading(true)

    const user = chariotResponse.detail.user
    const grantIntent = chariotResponse.detail.grantIntent
    const workflowSessionId = chariotResponse.detail.workflowSessionId

    let token = orderToken
    if (!token) {
      // Single-step: no Continue prelude, so JIT-saveDraft now.
      // useRecord owns the order_id-via-references plumbing, so a
      // second call from the same donor would update the same Order
      // rather than creating a duplicate.
      const { errors: draftErrors, token: draftToken } = await record.saveDraft({
        principalOnly: true,
        order: { payment_source: paymentMethod },
      })
      if (draftErrors) {
        setErrors(draftErrors)
        setLoading(false)
        return
      }
      token = draftToken
    }

    // We only post what Chariot's onSuccess contributes — donor
    // identity + the realized donation_amount. project_id was captured
    // on the saveDraft OrderItem and survives set_up_order_items via
    // the project_id carry-over in Order; plugin_data lives on
    // Order.data from saveDraft and doesn't change.
    const params = {
      order_id: token,
      api_key: apiKey,
      workflow_session_id: workflowSessionId,
      amount: grantIntent.amount,
      order: {
        user_first_name: user?.firstName,
        user_last_name: user?.lastName,
        email: user?.email || 'tech@pledgeling.com',
        donation_amount: grantIntent.amount / 100,
      },
    }

    fetch(submitUrl, {
      method: 'POST',
      headers: csrfHeaders(),
      body: JSON.stringify(params)
    })
    .then(response => response.json().then(data => {
      if (response.ok) {
        // Unwrap the serialized order rather than nesting it under `.order` —
        // daf_donations#create renders { order:, token: }. Matches v7
        // (steps/DafPayment.jsx) and v6's own crypto path (crypto/Info.jsx),
        // so amount + currency reach the completed donation and the parent-page
        // donationCompleted payload is identical across widget versions.
        setDonation({ ...data.order, token: data.token, partnerItem })
      } else {
        setErrors(data.errors)
      }
    })
    .catch(error => {
      // .catch only fires on network/JSON failure, so we genuinely
      // don't know whether the inline ChariotApi call ran. A "please
      // try again" message would risk a duplicate grantIntent if it
      // did; this softer copy commits to neither outcome.
      setErrors({ base: [ i18n.t('errors.donation_unconfirmed') ] })
      console.error(error)
    }))
    .finally(() => {
      setLoading(false)
    })
  }

  return loading
  ? <div
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: '48px' }}
    >
      <FontAwesomeIcon icon={faSpinner} pulse className="ms-2" />
    </div>
  : <div style={{ minHeight: '48px' }}>
      <InvalidForm>{errors?.base?.[0]}</InvalidForm>
      {connectId && (
        <ChariotConnect cid={connectId} onSuccess={onSuccess} />
      )}
    </div>
}

export default Daf
